import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';

type RoleType = 'Yönetici' | 'Danışma' | 'Personel';

interface User {
  id: string;
  name: string;
  email: string;
  role: RoleType;
  active: boolean;
}

const mockUsers: User[] = [
  { id: 'usr-001', name: 'Ali Yıldırım', email: 'ali.yildirim@kuguspa.com', role: 'Yönetici', active: true },
  { id: 'usr-002', name: 'Seda Avcı', email: 'seda.avci@kuguspa.com', role: 'Danışma', active: true },
  { id: 'usr-003', name: 'Ayşe Korkmaz', email: 'ayse.korkmaz@kuguspa.com', role: 'Personel', active: true },
  { id: 'usr-004', name: 'Mehmet Sarı', email: 'mehmet.sari@kuguspa.com', role: 'Personel', active: true },
];

const rolePermissions: Record<RoleType, string[]> = {
  'Yönetici': ['Tüm ekranlar', 'Raporlar', 'Kasa', 'Prim onaylama', 'Abonelik durumu', 'Ayarlar'],
  'Danışma': ['Randevu oluşturma', 'Müşteri kaydı', 'WhatsApp teyidi', 'Temel kasa girişi'],
  'Personel': ['Kendi randevuları', 'Kendi hizmetleri', 'Kendi prim durumu'],
};

export default function AyarlarPage() {
  const { theme, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<'business' | 'theme' | 'users' | 'hours' | 'categories' | 'templates' | 'subscription' | 'notifications'>('business');
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [businessForm, setBusinessForm] = useState({
    name: 'Kuğu Spa Wellness',
    address: 'Etiler, İstanbul',
    phone: '+90 212 555 6677',
    email: 'info@kuguspa.com',
    taxNumber: '1234567890',
    workingHours: '09:00 - 21:00',
  });

  const handleToggleUser = (id: string) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, active: !u.active } : u));
  };

  const sections = [
    { id: 'business' as const, label: 'İşletme Bilgileri', icon: 'ri-building-line' },
    { id: 'theme' as const, label: 'Tema Seçimi', icon: 'ri-paint-brush-line' },
    { id: 'users' as const, label: 'Kullanıcılar ve Yetkiler', icon: 'ri-team-line' },
    { id: 'hours' as const, label: 'Randevu Saatleri', icon: 'ri-time-line' },
    { id: 'categories' as const, label: 'Hizmet Kategorileri', icon: 'ri-drop-line' },
    { id: 'templates' as const, label: 'WhatsApp/SMS Şablonları', icon: 'ri-whatsapp-line' },
    { id: 'subscription' as const, label: 'Abonelik Görüntüleme', icon: 'ri-vip-diamond-line' },
    { id: 'notifications' as const, label: 'Bildirim Ayarları', icon: 'ri-notification-3-line' },
  ];

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Ayarlar</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Kuğu Spa panel ayarları</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="spa-card p-2 space-y-1">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-spa-sm text-sm font-medium transition-all cursor-pointer ${activeSection === section.id ? 'text-white' : ''}`}
                style={activeSection === section.id
                  ? { background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }
                  : { background: 'transparent', color: 'var(--text-secondary)' }
                }
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${section.icon} text-base`} />
                </div>
                <span className="truncate">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Business Info */}
          {activeSection === 'business' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>İşletme Bilgileri</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'İşletme Adı', key: 'name' as const },
                  { label: 'Adres', key: 'address' as const },
                  { label: 'Telefon', key: 'phone' as const },
                  { label: 'E-posta', key: 'email' as const },
                  { label: 'Vergi Numarası', key: 'taxNumber' as const },
                  { label: 'Çalışma Saatleri', key: 'workingHours' as const },
                ].map(field => (
                  <div key={field.key}>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>{field.label}</label>
                    <input
                      className="spa-input"
                      value={businessForm[field.key]}
                      onChange={e => setBusinessForm({ ...businessForm, [field.key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <button className="spa-btn-primary text-sm">Kaydet</button>
              </div>
            </div>
          )}

          {/* Theme */}
          {activeSection === 'theme' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Tema Seçimi</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => theme !== 'light' && toggleTheme()}
                  className={`p-4 rounded-spa-sm border-2 transition-all cursor-pointer text-left ${theme === 'light' ? 'border-[#C9A962]' : 'border-transparent'}`}
                  style={{ background: '#FAF6F0' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>Spa Premium Light</span>
                    {theme === 'light' && <i className="ri-check-line" style={{ color: '#C9A962' }} />}
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full" style={{ background: '#FAF6F0', border: '1px solid #E5DDD3' }} />
                    <div className="w-6 h-6 rounded-full" style={{ background: '#C9A962' }} />
                    <div className="w-6 h-6 rounded-full" style={{ background: '#2D5F3E' }} />
                  </div>
                </button>
                <button
                  onClick={() => theme !== 'dark' && toggleTheme()}
                  className={`p-4 rounded-spa-sm border-2 transition-all cursor-pointer text-left ${theme === 'dark' ? 'border-[#C9A962]' : 'border-transparent'}`}
                  style={{ background: '#1A1A20' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold" style={{ color: '#E8E4DC' }}>Luxury Dark</span>
                    {theme === 'dark' && <i className="ri-check-line" style={{ color: '#C9A962' }} />}
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full" style={{ background: '#1A1A20', border: '1px solid #333' }} />
                    <div className="w-6 h-6 rounded-full" style={{ background: '#C9A962' }} />
                    <div className="w-6 h-6 rounded-full" style={{ background: '#3E7A52' }} />
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Users */}
          {activeSection === 'users' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Kullanıcılar ve Yetkiler</h3>
                <button className="spa-btn-primary text-xs px-3 py-2">Yeni Kullanıcı</button>
              </div>
              <div className="space-y-3">
                {users.map(user => (
                  <div key={user.id} className="p-3 rounded-spa-sm border flex items-center justify-between" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{ background: user.role === 'Yönetici' ? '#8B5CF6' : user.role === 'Danışma' ? '#3B82F6' : 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className="spa-badge text-[10px]" style={{ background: user.role === 'Yönetici' ? 'rgba(139,92,246,0.12)' : user.role === 'Danışma' ? 'rgba(59,130,246,0.12)' : 'rgba(16,185,129,0.12)', color: user.role === 'Yönetici' ? '#8B5CF6' : user.role === 'Danışma' ? '#3B82F6' : '#10B981' }}>
                            {user.role}
                          </span>
                          {rolePermissions[user.role].map((perm, i) => (
                            <span key={i} className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{perm}{i < rolePermissions[user.role].length - 1 ? ',' : ''}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleUser(user.id)}
                      className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer"
                      style={{ background: user.active ? 'rgba(16,185,129,0.12)' : 'rgba(156,163,175,0.12)' }}
                    >
                      <i className={user.active ? 'ri-eye-line' : 'ri-eye-off-line'} style={{ color: user.active ? '#10B981' : '#9CA3AF' }} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Role Legend */}
              <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Yetki Açıklamaları</p>
                <div className="space-y-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <p><span className="font-medium" style={{ color: '#8B5CF6' }}>Yönetici:</span> Tüm ekranları görür, raporları ve kasayı görür, primleri onaylar, abonelik durumunu görür.</p>
                  <p><span className="font-medium" style={{ color: '#3B82F6' }}>Danışma:</span> Randevu oluşturur, müşteri kaydı açar, WhatsApp teyidi gönderir, temel kasa girişi yapar.</p>
                  <p><span className="font-medium" style={{ color: '#10B981' }}>Personel:</span> Kendi randevularını, kendi hizmetlerini ve kendi prim durumunu görür.</p>
                </div>
              </div>
            </div>
          )}

          {/* Hours */}
          {activeSection === 'hours' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Randevu Saat Ayarları</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'].map(day => (
                  <div key={day} className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>{day}</p>
                    <div className="space-y-2">
                      <input className="spa-input text-xs py-1.5" defaultValue="09:00" />
                      <input className="spa-input text-xs py-1.5" defaultValue="21:00" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <button className="spa-btn-primary text-sm">Kaydet</button>
              </div>
            </div>
          )}

          {/* Categories */}
          {activeSection === 'categories' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Hizmet Kategorileri</h3>
              <div className="flex flex-wrap gap-2">
                {["Masaj", "Spa", "Kese Köpük", "Sauna", "Buhar", "Tuz Odası", "Paket Hizmet"].map(cat => (
                  <div key={cat} className="flex items-center gap-2 px-3 py-2 rounded-spa-sm text-sm" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                    <i className="ri-drop-line" style={{ color: 'var(--gold)' }} />
                    {cat}
                    <button className="w-5 h-5 flex items-center justify-center cursor-pointer" style={{ color: '#EF4444' }}><i className="ri-close-line text-xs" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="spa-input text-sm flex-1" placeholder="Yeni kategori adı..." />
                <button className="spa-btn-primary text-sm">Ekle</button>
              </div>
            </div>
          )}

          {/* Templates */}
          {activeSection === 'templates' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>WhatsApp/SMS Şablon Ayarları</h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Şablon yönetimi için WhatsApp / SMS ekranını kullanın.</p>
              <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Mevcut şablon sayısı: 10</p>
              </div>
            </div>
          )}

          {/* Subscription */}
          {activeSection === 'subscription' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Abonelik Görüntüleme Ayarları</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Dashboard abonelik kartı</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Dashboard'da abonelik durumu mini kartı göster</p>
                  </div>
                  <div className="w-11 h-6 rounded-full relative cursor-pointer" style={{ background: '#10B981' }}>
                    <div className="absolute right-1 top-1 w-4 h-4 rounded-full bg-white" />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Ödeme hatırlatma bildirimi</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Ödeme beklemedeyse bildirim göster</p>
                  </div>
                  <div className="w-11 h-6 rounded-full relative cursor-pointer" style={{ background: '#10B981' }}>
                    <div className="absolute right-1 top-1 w-4 h-4 rounded-full bg-white" />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Fiyat bilgisi gösterimi</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Abonelik ekranında fiyat/ödeme tutarı gösterme</p>
                  </div>
                  <div className="w-11 h-6 rounded-full relative cursor-pointer" style={{ background: '#9CA3AF' }}>
                    <div className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeSection === 'notifications' && (
            <div className="spa-card p-4 md:p-5 space-y-4 animate-fade-in">
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Bildirim Ayarları</h3>
              <div className="space-y-3">
                {[
                  { label: 'Randevu hatırlatmaları', desc: 'Randevu öncesi otomatik hatırlatma', active: true },
                  { label: 'Paket bitiş uyarıları', desc: 'Seans sayısı azalan müşteri bildirimi', active: true },
                  { label: 'Açık ödeme hatırlatmaları', desc: 'Ödeme bekleyen işlem bildirimi', active: true },
                  { label: 'Güvenlik uyarıları', desc: 'Engelli müşteri girişi bildirimi', active: true },
                  { label: 'Stok kritik seviye', desc: 'Stok azalan ürün bildirimi', active: false },
                  { label: 'Günlük özet raporu', desc: 'Her gün sonunda e-posta özeti', active: false },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
                    </div>
                    <div className="w-11 h-6 rounded-full relative cursor-pointer flex-shrink-0" style={{ background: item.active ? '#10B981' : '#9CA3AF' }}>
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${item.active ? 'right-1' : 'left-1'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}